import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
  } from "react";
  
  import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    Paper,
    Stack,
    TextField,
    Typography,
  } from "@mui/material";
  
  import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
  import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
  import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
  import SendRoundedIcon from "@mui/icons-material/SendRounded";
  
  import { useStyle } from "../../context/styleContext/styleContext";
  
  const API_BASE =
    process.env.NODE_ENV === "development"
      ? "http://localhost:8080/api/press-email"
      : "https://api.parishaudio.com/api/press-email";
  
  const TEST_EMAIL = "nate.j.parish@gmail.com";
  
  export default function PressEmailPage() {
    const { style } = useStyle();
  
    const token = useMemo(
      () => localStorage.getItem("token") || "",
      []
    );
  
    const [recipientCount, setRecipientCount] =
      useState(null);
  
    const [campaign, setCampaign] = useState({
      subject: "",
      productUrl: "",
      from: "",
    });
  
    const [delayMs, setDelayMs] =
      useState("250");
  
    const [confirmFinalSend, setConfirmFinalSend] =
      useState(false);
  
    const [loadingKey, setLoadingKey] =
      useState("");
  
    const [lastResults, setLastResults] =
      useState(null);
  
    const [status, setStatus] = useState({
      type: "",
      message: "",
    });
  
    const commonFieldSx = useMemo(
      () => ({
        "& .MuiOutlinedInput-root": {
          color: style.color4,
          backgroundColor:
            "rgba(255,255,255,0.03)",
          borderRadius: "8px",
        },
  
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: style.color2,
        },
  
        "&:hover .MuiOutlinedInput-notchedOutline":
          {
            borderColor: style.color3,
          },
  
        "& .Mui-focused .MuiOutlinedInput-notchedOutline":
          {
            borderColor: style.accentGreen,
          },
  
        "& .MuiInputLabel-root": {
          color: style.color3,
        },
  
        "& .MuiInputLabel-root.Mui-focused": {
          color: style.color4,
        },
  
        "& .MuiFormHelperText-root": {
          color: style.color3,
          marginLeft: 0,
        },
      }),
      [style]
    );
  
    const makeRequest = useCallback(
      async ({
        key,
        endpoint,
        requestBody = {},
        clearResults = true,
      }) => {
        if (!token) {
          setStatus({
            type: "error",
            message:
              "You must be logged in as an admin.",
          });
  
          return null;
        }
  
        setLoadingKey(key);
  
        setStatus({
          type: "",
          message: "",
        });
  
        if (clearResults) {
          setLastResults(null);
        }
  
        try {
          const response = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Content-Type":
                "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              token,
              ...requestBody,
            }),
          });
  
          const data = await response
            .json()
            .catch(() => ({}));
  
          if (!response.ok) {
            throw new Error(
              data?.message ||
                data?.error ||
                `Request failed with status ${response.status}`
            );
          }
  
          if (data?.campaign) {
            setCampaign({
              subject:
                data.campaign.subject || "",
              productUrl:
                data.campaign.productUrl || "",
              from: data.campaign.from || "",
            });
          }
  
          if (data?.results) {
            setLastResults(data.results);
          }
  
          setStatus({
            type: "success",
            message:
              data?.message ||
              "Request completed successfully.",
          });
  
          return data;
        } catch (error) {
          setStatus({
            type: "error",
            message:
              error.message ||
              "Request failed.",
          });
  
          return null;
        } finally {
          setLoadingKey("");
        }
      },
      [token]
    );
  
    const loadRecipientCount =
      useCallback(async () => {
        const data = await makeRequest({
          key: "count",
          endpoint: `${API_BASE}/count`,
          clearResults: false,
        });
  
        if (data?.results) {
          setRecipientCount(
            data.results.totalFound ?? 0
          );
        }
      }, [makeRequest]);
  
    const sendTestEmail = async () => {
      await makeRequest({
        key: "test",
        endpoint: `${API_BASE}/test`,
      });
    };
  
    const sendFinalEmail = async () => {
      if (!confirmFinalSend) {
        setStatus({
          type: "error",
          message:
            "Check the final confirmation box before sending.",
        });
  
        return;
      }
  
      if (
        recipientCount === null ||
        recipientCount <= 0
      ) {
        setStatus({
          type: "error",
          message:
            "No press contacts are currently available for this send.",
        });
  
        return;
      }
  
      const typedConfirmation =
        window.prompt(
          `This will send the S1 press release to ${recipientCount} press contacts. Type "SEND" to continue.`
        );
  
      if (typedConfirmation !== "SEND") {
        setStatus({
          type: "error",
          message:
            "Send canceled. The confirmation text did not match.",
        });
  
        return;
      }
  
      const data = await makeRequest({
        key: "final",
        endpoint: `${API_BASE}/send`,
        requestBody: {
          delayMs:
            Number(delayMs) || 250,
          confirmSend: true,
          confirmText: "SEND",
        },
      });
  
      if (data?.results) {
        setConfirmFinalSend(false);
      }
    };
  
    useEffect(() => {
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
  
      loadRecipientCount();
    }, [loadRecipientCount]);
  
    const renderLoadingIcon = (
      loadingName,
      normalIcon
    ) => {
      if (loadingKey === loadingName) {
        return (
          <CircularProgress
            size={17}
            sx={{
              color: style.color4,
            }}
          />
        );
      }
  
      return normalIcon;
    };
  
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: "black",
          px: {
            xs: 1.25,
            sm: 1.5,
            md: 2.5,
          },
          py: {
            xs: 1.5,
            md: 3,
          },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "1100px",
            mx: "auto",
          }}
        >
          <Stack spacing={2}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor:
                  style.color1,
                border: `1px solid ${style.color2}`,
              }}
            >
              <Box
                sx={{
                  px: {
                    xs: 1.5,
                    sm: 2,
                    md: 3,
                  },
                  py: {
                    xs: 2,
                    md: 2.75,
                  },
                  backgroundColor:
                    style.darkPanel,
                  borderBottom: `1px solid ${style.color2}`,
                }}
              >
                <Stack spacing={1}>
                  <Chip
                    icon={
                      <ArticleRoundedIcon />
                    }
                    label="Admin Press Tools"
                    sx={{
                      alignSelf:
                        "flex-start",
                      borderRadius: "5px",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor:
                        "rgba(255,255,255,0.06)",
                    }}
                  />
  
                  <Typography
                    component="h1"
                    sx={{
                      color: style.color4,
                      fontSize: {
                        xs: "1.9rem",
                        md: "2.5rem",
                      },
                      fontWeight: 800,
                      lineHeight: 1.05,
                      letterSpacing:
                        "-0.03em",
                    }}
                  >
                    S1 Press Release Email
                  </Typography>
  
                  <Typography
                    sx={{
                      color: style.color3,
                      lineHeight: 1.65,
                      maxWidth: "800px",
                    }}
                  >
                    The subject, body, links,
                    and HTML are now constructed
                    by the backend. Use this page
                    to test the finished email
                    and send it to every contact
                    with{" "}
                    <strong>
                      sendPREmails: true
                    </strong>
                    .
                  </Typography>
                </Stack>
              </Box>
  
              <Box
                sx={{
                  p: {
                    xs: 1.5,
                    sm: 2,
                    md: 3,
                  },
                }}
              >
                <Stack spacing={2}>
                  {!token ? (
                    <Alert
                      severity="error"
                      sx={{
                        color: style.color4,
                        backgroundColor:
                          "rgba(140,30,30,0.25)",
                        border:
                          "1px solid rgba(255,255,255,0.08)",
                        "& .MuiAlert-icon":
                          {
                            color:
                              style.color4,
                          },
                      }}
                    >
                      No admin token was
                      found. Sign in again
                      before using this page.
                    </Alert>
                  ) : null}
  
                  <Stack
                    direction={{
                      xs: "column",
                      md: "row",
                    }}
                    spacing={1}
                    alignItems={{
                      xs: "stretch",
                      md: "center",
                    }}
                  >
                    <Chip
                      label={`Press contacts: ${
                        recipientCount === null
                          ? "Loading..."
                          : recipientCount
                      }`}
                      sx={{
                        color: style.color4,
                        backgroundColor:
                          "rgba(38,166,104,0.18)",
                        fontWeight: 700,
                      }}
                    />
  
                    <Button
                      onClick={
                        loadRecipientCount
                      }
                      disabled={
                        !!loadingKey ||
                        !token
                      }
                      startIcon={renderLoadingIcon(
                        "count",
                        <RefreshRoundedIcon />
                      )}
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor:
                          "rgba(255,255,255,0.05)",
                        border:
                          "1px solid rgba(255,255,255,0.08)",
                        "&:hover": {
                          backgroundColor:
                            "rgba(255,255,255,0.08)",
                        },
                      }}
                    >
                      {loadingKey === "count"
                        ? "Loading..."
                        : "Refresh Count"}
                    </Button>
                  </Stack>
  
                  <Paper
                    elevation={0}
                    sx={{
                      p: {
                        xs: 1.5,
                        md: 2,
                      },
                      borderRadius: "14px",
                      backgroundColor:
                        style.darkPanel ||
                        "rgba(9,12,18,0.45)",
                      border:
                        "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Typography
                        sx={{
                          color: style.color4,
                          fontWeight: 800,
                          fontSize: "1.1rem",
                        }}
                      >
                        Backend Campaign
                      </Typography>
  
                      <Box>
                        <Typography
                          sx={{
                            color: style.color3,
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            textTransform:
                              "uppercase",
                            letterSpacing:
                              "0.04em",
                          }}
                        >
                          From
                        </Typography>
  
                        <Typography
                          sx={{
                            color: style.color4,
                            overflowWrap:
                              "anywhere",
                          }}
                        >
                          {campaign.from ||
                            "Nathaniel Parish | Parish Audio <press@parishaudio.com>"}
                        </Typography>
                      </Box>
  
                      <Box>
                        <Typography
                          sx={{
                            color: style.color3,
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            textTransform:
                              "uppercase",
                            letterSpacing:
                              "0.04em",
                          }}
                        >
                          Subject
                        </Typography>
  
                        <Typography
                          sx={{
                            color: style.color4,
                          }}
                        >
                          {campaign.subject ||
                            "Parish Audio releases S1 De-Esser — free for a limited time"}
                        </Typography>
                      </Box>
  
                      <Box>
                        <Typography
                          sx={{
                            color: style.color3,
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            textTransform:
                              "uppercase",
                            letterSpacing:
                              "0.04em",
                          }}
                        >
                          Product Page
                        </Typography>
  
                        <Typography
                          sx={{
                            color: style.color4,
                            overflowWrap:
                              "anywhere",
                          }}
                        >
                          {campaign.productUrl ||
                            "https://parishaudio.com/products/s1-de-esser"}
                        </Typography>
                      </Box>
  
                      <Typography
                        sx={{
                          color: style.color3,
                          fontSize: "0.9rem",
                          lineHeight: 1.6,
                        }}
                      >
                        To change the subject,
                        copy, or links, edit the
                        constants and{" "}
                        <strong>
                          buildS1PressEmail
                        </strong>{" "}
                        function in the backend
                        controller.
                      </Typography>
                    </Stack>
                  </Paper>
  
                  <TextField
                    fullWidth
                    label="Delay Between Emails"
                    value={delayMs}
                    onChange={(event) =>
                      setDelayMs(
                        event.target.value
                      )
                    }
                    type="number"
                    sx={commonFieldSx}
                    inputProps={{
                      min: 0,
                      max: 5000,
                    }}
                    helperText="Delay in milliseconds. 250ms is suitable for a small press list."
                  />
  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          confirmFinalSend
                        }
                        onChange={(event) =>
                          setConfirmFinalSend(
                            event.target
                              .checked
                          )
                        }
                        sx={{
                          color:
                            style.color3,
                          "&.Mui-checked": {
                            color:
                              style.accentGreen,
                          },
                        }}
                      />
                    }
                    label={`I understand that the final send will email all ${
                      recipientCount ?? ""
                    } contacts currently enabled for press emails.`}
                    sx={{
                      color: style.color4,
                      alignItems:
                        "flex-start",
                      "& .MuiFormControlLabel-label":
                        {
                          lineHeight: 1.55,
                        },
                    }}
                  />
                </Stack>
              </Box>
            </Paper>
  
            {status.message ? (
              <Alert
                severity={
                  status.type === "error"
                    ? "error"
                    : "success"
                }
                sx={{
                  borderRadius: "12px",
                  color: style.color4,
                  backgroundColor:
                    status.type ===
                    "error"
                      ? "rgba(140,30,30,0.25)"
                      : "rgba(38,166,104,0.18)",
                  border:
                    "1px solid rgba(255,255,255,0.08)",
                  "& .MuiAlert-icon": {
                    color: style.color4,
                  },
                }}
              >
                {status.message}
              </Alert>
            ) : null}
  
            {lastResults ? (
              <Paper
                elevation={0}
                sx={{
                  p: {
                    xs: 1.5,
                    md: 2,
                  },
                  borderRadius: "14px",
                  backgroundColor:
                    style.color1,
                  border: `1px solid ${style.color2}`,
                }}
              >
                <Stack spacing={1.25}>
                  <Typography
                    sx={{
                      color: style.color4,
                      fontWeight: 800,
                      fontSize: "1.05rem",
                    }}
                  >
                    Last Request Results
                  </Typography>
  
                  <Stack
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    spacing={1}
                    flexWrap="wrap"
                  >
                    {lastResults.totalFound !==
                    undefined ? (
                      <Chip
                        label={`Found: ${lastResults.totalFound}`}
                        sx={{
                          color:
                            style.color4,
                          backgroundColor:
                            "rgba(255,255,255,0.06)",
                        }}
                      />
                    ) : null}
  
                    {lastResults.sent !==
                    undefined ? (
                      <Chip
                        label={`Sent: ${lastResults.sent}`}
                        sx={{
                          color:
                            style.color4,
                          backgroundColor:
                            "rgba(38,166,104,0.18)",
                        }}
                      />
                    ) : null}
  
                    {lastResults.failed !==
                    undefined ? (
                      <Chip
                        label={`Failed: ${lastResults.failed}`}
                        sx={{
                          color:
                            style.color4,
                          backgroundColor:
                            lastResults.failed >
                            0
                              ? "rgba(140,30,30,0.25)"
                              : "rgba(255,255,255,0.06)",
                        }}
                      />
                    ) : null}
                  </Stack>
  
                  {Array.isArray(
                    lastResults.failures
                  ) &&
                  lastResults.failures
                    .length > 0 ? (
                    <Box>
                      <Typography
                        sx={{
                          color:
                            style.color3,
                          fontSize:
                            "0.9rem",
                          mb: 0.75,
                        }}
                      >
                        Failed recipients:
                      </Typography>
  
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1.25,
                          borderRadius:
                            "10px",
                          backgroundColor:
                            "rgba(0,0,0,0.25)",
                          color:
                            style.color3,
                          fontSize:
                            "0.78rem",
                          overflowX:
                            "auto",
                        }}
                      >
                        {JSON.stringify(
                          lastResults.failures,
                          null,
                          2
                        )}
                      </Box>
                    </Box>
                  ) : null}
                </Stack>
              </Paper>
            ) : null}
  
            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              spacing={1.5}
            >
              <Button
                fullWidth
                variant="contained"
                onClick={sendTestEmail}
                disabled={
                  !!loadingKey || !token
                }
                startIcon={renderLoadingIcon(
                  "test",
                  <MarkEmailReadRoundedIcon />
                )}
                sx={{
                  minHeight: 52,
                  borderRadius: "5px",
                  textTransform: "none",
                  fontWeight: 800,
                  color: style.color4,
                  backgroundColor:
                    style.color2,
                  "&:hover": {
                    backgroundColor:
                      style.color2,
                    filter:
                      "brightness(1.08)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      "rgba(255,255,255,0.12)",
                    color: style.color3,
                  },
                }}
              >
                {loadingKey === "test"
                  ? "Sending Test..."
                  : `Send Test to ${TEST_EMAIL}`}
              </Button>
  
              <Button
                fullWidth
                variant="contained"
                onClick={sendFinalEmail}
                disabled={
                  !!loadingKey ||
                  !token ||
                  !confirmFinalSend ||
                  !recipientCount
                }
                startIcon={renderLoadingIcon(
                  "final",
                  <SendRoundedIcon />
                )}
                sx={{
                  minHeight: 52,
                  borderRadius: "5px",
                  textTransform: "none",
                  fontWeight: 800,
                  color: style.color4,
                  backgroundColor:
                    style.accentGreen,
                  "&:hover": {
                    backgroundColor:
                      style.accentGreen,
                    filter:
                      "brightness(1.08)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      "rgba(255,255,255,0.12)",
                    color: style.color3,
                  },
                }}
              >
                {loadingKey === "final"
                  ? "Sending Press Emails..."
                  : `Send to All ${
                      recipientCount ?? ""
                    } Press Contacts`}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    );
  }